/*
  Mechlands sale page (sections/mls-*.liquid).

  Times are written in the editor as "YYYY-MM-DD HH:MM" in the campaign timezone; the hero
  section sets window.mlsTz (hours from UTC, default 8). Everything below switches on its own
  at those moments, so nobody has to edit the page at midnight.

  - [data-mls-window data-from data-to]  gets data-state = soon | live | done, and shows or hides
    its [data-show="soon live …"] children. Elements inside it with data-mls-count="next" count
    down to whichever boundary comes next.
  - [data-mls-phases]  holds [data-phase data-from] panes: the last one that has started is shown.
  - [data-mls-count data-target]  a countdown to a fixed time.
  - [data-mls-deals]  the sitewide deals grid: category pills, discount / brand / price / sort
    menus and "View more". Cards are only shown, hidden and re-ordered, never rebuilt.
*/
(function () {
  if (window.mlsSaleLoaded) return;
  window.mlsSaleLoaded = true;

  const tz = () => (typeof window.mlsTz === 'number' ? window.mlsTz : 8);
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const pad = (n) => String(n).padStart(2, '0');

  /* "2026-10-26 00:00" in the campaign timezone -> epoch ms (NaN when blank or malformed) */
  function ts(v) {
    const m = String(v || '').trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/);
    if (!m) return NaN;
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0)) - tz() * 3600e3;
  }
  /* epoch ms -> "Oct 26", read in the campaign timezone */
  function day(ms) {
    const d = new Date(ms + tz() * 3600e3);
    return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }
  const setText = (el, t) => { if (el && el.textContent !== t) el.textContent = t; };
  const setHidden = (el, h) => { if (el && el.hidden !== h) el.hidden = h; };

  /* ---------- countdowns ---------- */
  function paintCount(el, target, now) {
    const left = Math.max(0, Math.floor((target - now) / 1000));
    const parts = { d: Math.floor(left / 86400), h: Math.floor(left / 3600) % 24, m: Math.floor(left / 60) % 60, s: left % 60 };
    el.querySelectorAll('[data-cd]').forEach((n) => setText(n, pad(parts[n.dataset.cd])));
    setHidden(el, !(target > now));
  }

  /* ---------- windows: soon / live / done ---------- */
  function paintWindow(w, now) {
    const a = ts(w.dataset.from), b = ts(w.dataset.to);
    let st = 'live';
    if (a && now < a) st = 'soon';
    else if (b && now >= b) st = 'done';
    if (w.dataset.state !== st) {
      w.dataset.state = st;
      w.querySelectorAll('[data-show]').forEach((el) => {
        if (el.closest('[data-mls-window]') !== w) return;
        setHidden(el, !el.dataset.show.split(' ').includes(st));
      });
    }
    /* buttons that only work while the window is open; set on every tick, since theme scripts
       (product-card-swatch.js) strip disabled off every submit button on the page */
    w.querySelectorAll('[data-live-only]').forEach((el) => {
      if (el.closest('[data-mls-window]') !== w) return;
      const off = st !== 'live' || el.dataset.soldOut === 'true';
      if (el.disabled !== off) el.disabled = off;
    });
    w.querySelectorAll('[data-date="from"]').forEach((el) => { if (a) setText(el, day(a)); });
    w.querySelectorAll('[data-date="to"]').forEach((el) => { if (b) setText(el, day(b - 1)); });
    w.querySelectorAll('[data-mls-count="next"]').forEach((el) => {
      if (el.closest('[data-mls-window]') !== w) return;
      const target = st === 'soon' ? a : st === 'live' ? b : NaN;
      el.dataset.mode = st;
      paintCount(el, target, now);
    });
  }

  /* ---------- hero phases ---------- */
  function paintPhases(root, now) {
    const panes = [...root.querySelectorAll('[data-phase]')];
    let on = panes[0];
    panes.forEach((p) => { const a = ts(p.dataset.from); if (a && now >= a) on = p; });
    if (root._on !== on) {
      root._on = on;
      panes.forEach((p) => setHidden(p, p !== on));
    }
    /* timeline: the running step lights up, finished ones dim */
    root.querySelectorAll('[data-step]').forEach((s) => {
      const a = ts(s.dataset.from), b = ts(s.dataset.until);
      s.classList.toggle('is-on', !!(a && now >= a && (!b || now < b)));
      s.classList.toggle('is-past', !!(b && now >= b));
    });
  }

  function tick() {
    const now = Date.now();
    document.querySelectorAll('[data-mls-phases]').forEach((r) => paintPhases(r, now));
    document.querySelectorAll('[data-mls-window]').forEach((w) => paintWindow(w, now));
    document.querySelectorAll('[data-mls-count][data-target]').forEach((el) => paintCount(el, ts(el.dataset.target), now));
  }

  /* ---------- sitewide deals ---------- */
  function initDeals(root) {
    if (root.dataset.mlsReady) return;
    root.dataset.mlsReady = '1';
    const grid = root.querySelector('[data-d-grid]');
    if (!grid) return;
    /* a tier is a number ("30") or a range ("40-50"); a product joins the tier whose range holds its
       discount, otherwise the nearest one (a 49% markdown still sits in the 50% tier) */
    const tiers = String(root.dataset.tiers || '').split(',').map((k) => k.trim()).filter(Boolean).map((key) => {
      const n = (key.match(/\d+(?:\.\d+)?/g) || []).map(Number);
      return { key, lo: Math.min(...n), hi: Math.max(...n) };
    }).filter((t) => t.hi > 0);
    const tierOf = (off) => {
      if (!(off > 0) || !tiers.length) return '';
      const gap = (t) => (off < t.lo ? t.lo - off : off > t.hi ? off - t.hi : 0);
      let best = tiers[0];
      tiers.forEach((t) => { if (gap(t) < gap(best)) best = t; });
      return best.key;
    };
    const items = [...grid.querySelectorAll('[data-d-item]')].map((li) => ({
      li, i: +li.dataset.i, cats: li.dataset.cats || '|', price: +li.dataset.price || 0, off: +li.dataset.off || 0,
      vendor: li.dataset.vendor || '', name: li.dataset.title || '', date: +li.dataset.date || 0, tier: tierOf(+li.dataset.off || 0),
    }));
    const pills = [...root.querySelectorAll('[data-d-cat]')];
    const tierBtns = [...root.querySelectorAll('[data-d-tier]')];
    const sels = {};
    root.querySelectorAll('[data-d-sel]').forEach((x) => { sels[x.dataset.dSel] = x; });
    const more = root.querySelector('[data-d-more]'), empty = root.querySelector('[data-d-empty]'), count = root.querySelector('[data-d-count]');
    const size = Math.max(1, +root.dataset.page || 8);
    const st = { cat: '', tier: '', vendor: '', price: '', sort: '' };
    let pages = 1;

    /* cards past "Cards built with the page" arrive as a placeholder: fetch the real card the first time it is shown */
    const fill = (li) => {
      if (!li.dataset.lazy || li.dataset.loading) return;
      li.dataset.loading = '1';
      const u = new URL(li.dataset.lazy, location.origin);
      u.searchParams.set('section_id', 'mls-card');
      fetch(u, { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((html) => {
          const box = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
          if (!box || !box.firstElementChild) throw new Error('no card');
          li.replaceChildren(...[...box.childNodes].map((n) => document.adoptNode(n)));
          delete li.dataset.lazy;
        })
        .catch(() => {})
        .finally(() => { delete li.dataset.loading; });
    };

    /* tier cards show how many products each holds; empty tiers drop out of the menu */
    tierBtns.forEach((b) => {
      const n = items.filter((x) => x.tier === b.dataset.dTier).length;
      setText(b.querySelector('[data-d-tiern]'), n ? String(n) : '');
      b.classList.toggle('is-empty', !n);
    });
    if (sels.tier) [...sels.tier.options].forEach((o) => { if (o.value && !items.some((x) => x.tier === o.value)) o.remove(); });

    function apply() {
      let lo = 0, hi = Infinity;
      if (st.price) { const [a, b] = st.price.split('-'); lo = +a || 0; hi = b === '' ? Infinity : +b; }
      const list = items.filter((x) => (!st.cat || x.cats.includes('|' + st.cat + '|')) && (!st.tier || x.tier === st.tier)
        && (!st.vendor || x.vendor === st.vendor) && x.price >= lo && x.price < hi);
      const by = {
        low: (a, b) => a.price - b.price || a.i - b.i,
        high: (a, b) => b.price - a.price || a.i - b.i,
        off: (a, b) => b.off - a.off || a.i - b.i,
        az: (a, b) => a.name.localeCompare(b.name) || a.i - b.i,
        
      }[st.sort] || ((a, b) => a.i - b.i);
      list.sort(by);
      const on = new Set(list.slice(0, pages * size));
      on.forEach((x) => fill(x.li));
      list.forEach((x, k) => { const o = String(k); if (x.li.style.order !== o) x.li.style.order = o; });
      items.forEach((x) => setHidden(x.li, !on.has(x)));
      setHidden(more, list.length <= pages * size);
      setHidden(empty, list.length > 0);
      if (count) setText(count, (list.length === 1 ? root.dataset.one : root.dataset.many || '').replace('[count]', list.length));
      Object.values(sels).forEach((x) => x.closest('.mls-dl__sel').classList.toggle('is-set', x.value !== ''));
      tierBtns.forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.dTier === st.tier)));
    }
    pills.forEach((b) => b.addEventListener('click', () => {
      st.cat = b.dataset.dCat; pages = 1;
      pills.forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      apply();
    }));
    tierBtns.forEach((b) => b.addEventListener('click', () => {
      st.tier = st.tier === b.dataset.dTier ? '' : b.dataset.dTier; pages = 1;
      if (sels.tier) sels.tier.value = st.tier;
      apply();
      const top = root.querySelector('[data-d-bar]');
      if (top && top.getBoundingClientRect().top < 0) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
    Object.keys(sels).forEach((k) => sels[k].addEventListener('change', () => { st[k] = sels[k].value; pages = 1; apply(); }));
    if (more) more.addEventListener('click', () => { pages++; apply(); });
    const reset = root.querySelector('[data-d-reset]');
    if (reset) reset.addEventListener('click', () => {
      Object.keys(st).forEach((k) => { st[k] = ''; });
      pages = 1;
      Object.values(sels).forEach((x) => { x.value = ''; });
      pills.forEach((x) => x.setAttribute('aria-pressed', String(x.dataset.dCat === '')));
      apply();
    });
    pills.forEach((b) => {
      const n = b.querySelector('[data-d-catn]');
      setText(n, String(items.filter((x) => !b.dataset.dCat || x.cats.includes('|' + b.dataset.dCat + '|')).length));
    });
    apply();
  }

  /* ---------- "Plan my 11.11" (mls-planner), after Linsoul's sale planner ----------
     A floating button opens a dialog with two tools. Saving: a budget slider turns on each activity's
     row as the budget reaches its "Unlocks at", each with its own value (never a total). Key dates:
     the days something opens, what opens or ends, and Add to calendar (Google Calendar or .ics). */
  const DAY = 864e5;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const dUTC = (v) => { const m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(String(v || '').trim()); return m ? Date.UTC(+m[1], m[2] - 1, +m[3]) : null; };
  const make = (tag, cls, txt) => { const n = document.createElement(tag); if (cls) n.className = cls; if (txt != null) n.textContent = txt; return n; };

  /* ---------- add to calendar: a two-item menu (after EPOMAKER's s26Cal) ----------
     A .ics download alone opens whatever owns .ics on the device (Outlook on most Windows PCs) and
     does nothing useful on Android phones that only have Google Calendar, so the button opens a menu:
     Google Calendar (its prefilled "new event" page) or Apple / Outlook (.ics).
     cal.menu(button, { title, start, end (ms), details, url, alarm (minutes before), file }) */
  const cal = (() => {
    const z = (ms) => new Date(ms).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const tx = (v) => String(v || '').replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, (m) => '\\' + m);
    const endOf = (ev) => (ev.end && ev.end > ev.start ? ev.end : ev.start + 3600e3);
    const notes = (ev) => [ev.details, ev.url].filter(Boolean).join('\n\n');
    const ICON_G = '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="4.5" width="18" height="16.5" rx="3" fill="#fff" stroke="#DADCE0"/><path d="M3 9h18" stroke="#DADCE0"/><rect x="3" y="4.5" width="18" height="4.5" rx="2" fill="#4285F4"/><text x="12" y="18.6" text-anchor="middle" font-family="Arial,sans-serif" font-size="8.5" font-weight="700" fill="#4285F4">31</text></svg>';
    const ICON_F = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 4v11M7.5 10.5 12 15l4.5-4.5M5 19.5h14"/></svg>';
    let pop = null, owner = null;
    function ics(ev) {
      const alarm = ev.alarm == null ? 10 : ev.alarm;
      const body = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//MechLands//Sale//EN', 'CALSCALE:GREGORIAN', 'BEGIN:VEVENT',
        'UID:' + z(ev.start) + '-' + Math.random().toString(36).slice(2) + '@' + location.hostname,
        'DTSTAMP:' + z(Date.now()), 'DTSTART:' + z(ev.start), 'DTEND:' + z(endOf(ev)),
        'SUMMARY:' + tx(ev.title), 'DESCRIPTION:' + tx(notes(ev)), 'URL:' + (ev.url || ''),
        'BEGIN:VALARM', 'ACTION:DISPLAY', 'TRIGGER:-PT' + alarm + 'M', 'DESCRIPTION:' + tx(ev.title), 'END:VALARM',
        'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([body], { type: 'text/calendar;charset=utf-8' }));
      a.download = (ev.file || 'calendar') + '.ics';
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
    }
    const google = (ev) => 'https://calendar.google.com/calendar/render?action=TEMPLATE&text=' + encodeURIComponent(ev.title || '')
      + '&dates=' + z(ev.start) + '/' + z(endOf(ev)) + '&details=' + encodeURIComponent(notes(ev));
    function close(refocus) {
      if (!pop) return;
      pop.remove(); pop = null;
      removeEventListener('pointerdown', outside, true); removeEventListener('keydown', key, true);
      removeEventListener('scroll', shut, true); removeEventListener('resize', shut);
      if (owner) { owner.setAttribute('aria-expanded', 'false'); if (refocus) owner.focus(); owner = null; }
    }
    const shut = () => close(false);
    const outside = (e) => { if (pop && !pop.contains(e.target) && !owner.contains(e.target)) close(false); };
    const key = (e) => {
      if (!pop) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true); return; }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const items = [...pop.querySelectorAll('a,button')], i = items.indexOf(document.activeElement);
        e.preventDefault(); items[(i + (e.key === 'ArrowDown' ? 1 : items.length - 1)) % items.length].focus();
      }
    };
    function menu(btn, ev) {
      if (!ev || !ev.start) return;
      if (pop && owner === btn) { close(true); return; }
      close(false);
      pop = make('div', 'mls-cal'); pop.setAttribute('role', 'menu');
      const g = make('a'); g.setAttribute('role', 'menuitem');
      g.href = google(ev); g.target = '_blank'; g.rel = 'noopener';
      g.innerHTML = ICON_G + '<span>Google Calendar</span>';
      const f = make('button'); f.type = 'button'; f.setAttribute('role', 'menuitem');
      f.innerHTML = ICON_F + '<span>Apple / Outlook</span><small>.ics</small>';
      g.addEventListener('click', () => close(false));
      f.addEventListener('click', () => { ics(ev); close(true); });
      pop.append(g, f);
      /* inside a dialog the menu has to live in the dialog, or it renders underneath it */
      (btn.closest('dialog') || document.body).appendChild(pop);
      owner = btn; btn.setAttribute('aria-haspopup', 'menu'); btn.setAttribute('aria-expanded', 'true');
      const r = btn.getBoundingClientRect(), w = pop.offsetWidth, h = pop.offsetHeight;
      pop.style.left = Math.max(8, Math.min(r.left, innerWidth - w - 8)) + 'px';
      pop.style.top = (r.bottom + 8 + h <= innerHeight - 8 ? r.bottom + 8 : Math.max(8, r.top - h - 8)) + 'px';
      g.focus({ preventScroll: true });
      setTimeout(() => {
        addEventListener('pointerdown', outside, true); addEventListener('keydown', key, true);
        addEventListener('scroll', shut, { capture: true, passive: true }); addEventListener('resize', shut);
      });
    }
    return { menu };
  })();


  function initPlanner(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    let data;
    try { data = JSON.parse(root.querySelector('[data-plan-data]').textContent); } catch (e) { return; }
    const t = data.t || {};
    const items = (data.items || []).filter((x) => x && x.title);
    const dlg = root.querySelector('[data-plan-dialog]');
    const launch = root.querySelector('[data-plan-launch]');
    if (!dlg) return;
    const today = Math.floor((Date.now() + tz() * 3600e3) / DAY) * DAY;
    const isEnded = (it) => dUTC(it.to) != null && today > dUTC(it.to);
    const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
    const rich = (s) => esc(s).replace(/&lt;(\/?)(b|strong|em)&gt;/g, '<$1b>');
    const fill = (s, o) => String(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in o ? o[k] : m));
    const num = (v, d) => { const n = parseFloat(String(v == null ? '' : v).replace(/[^0-9.]/g, '')); return Number.isNaN(n) ? d : n; };
    const usd = (n) => '$' + (Math.round(n * 100) % 100 ? n.toFixed(2) : String(Math.round(n)));
    /* the Spend & Save section's own tiers (on the sale page, or passed in by the homepage copy) win over the planner's copy */
    const pageTiers = document.querySelector('[data-mls-spend-tiers]')?.dataset.mlsSpendTiers;
    const tiers = String(data.spendTiers || pageTiers || data.tiers || '').split(',').map((p) => p.split(':').map((x) => num(x, NaN)))
      .filter((p) => p.length === 2 && !Number.isNaN(p[0]) && !Number.isNaN(p[1]))
      .map(([min, save]) => ({ min, save })).sort((a, b) => a.min - b.min);

    /* rows */
    const ICON_ON = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
    const ICON_OFF = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V8a4 4 0 0 1 8 0v3"/></svg>';
    const makeRow = (it, list) => {
      const li = make('li', 'mls-plan__row');
      li.innerHTML = '<span class="mls-plan__row-ic"></span><div><p class="mls-plan__row-t"></p><p class="mls-plan__row-v"></p></div>';
      const tt = li.querySelector('.mls-plan__row-t');
      tt.textContent = it.title;
      if (it.dates) tt.append(make('small', '', it.dates));
      if (it.link) { const a = make('a', 'mls-plan__go', t.go || 'Go'); a.href = it.link; li.append(a); }
      li._it = it;
      list.append(li);
      return li;
    };
    const setRow = (li, state, html) => {
      if (isEnded(li._it)) { state = 'ended'; html = esc(t.ended); }
      if (li._s !== state) {
        li._s = state;
        li.classList.toggle('is-on', state === 'on');
        li.classList.toggle('is-ended', state === 'ended');
        li.querySelector('.mls-plan__row-ic').innerHTML = state === 'on' ? ICON_ON : ICON_OFF;
      }
      if (li._h !== html) { li._h = html; li.querySelector('.mls-plan__row-v').innerHTML = html; }
    };
    const spendText = (A) => {
      let cur = null;
      let next = null;
      tiers.forEach((x) => { if (A >= x.min) cur = x; else if (!next) next = x; });
      if (!cur) return next ? rich(fill(t.spendGap, { gap: usd(next.min - A), save: usd(next.save) })) : '';
      let s = fill(t.spend, { save: usd(cur.save) });
      if (next) s += ' ' + fill(t.spendNext, { gap: usd(next.min - A), save: usd(next.save) }).trim();
      return rich(s);
    };

    /* tool 1: saving */
    const savePanel = root.querySelector('[data-plan-panel="save"]');
    const range = savePanel.querySelector('[data-plan-range]');
    const out = savePanel.querySelector('[data-plan-amt]');
    const list = savePanel.querySelector('[data-plan-rows]');
    const tickBox = savePanel.querySelector('[data-plan-ticks]');
    const max = num(data.max, 400);
    const ROWS = items
      .filter((it) => String(it.need == null ? '' : it.need).trim() !== '')
      .map((it) => {
        const spend = it.key === 'spend';
        return { it, spend, need: spend && tiers.length ? tiers[0].min : num(it.need, 0), v: spend ? spendText : () => rich(it.value) };
      })
      .sort((a, b) => a.need - b.need);
    ROWS.forEach((r) => { r.li = makeRow(r.it, list); });
    const marks = [...new Set(tiers.map((x) => x.min))].filter((m) => m > 0 && m <= max).sort((a, b) => a - b);
    const ticks = marks.map((m) => { const s = make('span', 'mls-plan__tick', usd(m)); s.style.left = (m / max * 100) + '%'; tickBox.append(s); return { m, s }; });
    let A = -1;
    const set = (v) => {
      v = Math.max(0, Math.min(max, Math.round(v / 10) * 10));
      if (v === A) return;
      A = v;
      if (Number(range.value) !== v) range.value = v;
      range.style.setProperty('--p', (v / max * 100) + '%');
      out.textContent = usd(v);
      ticks.forEach((x) => x.s.classList.toggle('is-on', v >= x.m));
      ROWS.forEach((r) => {
        const on = v >= r.need;
        setRow(r.li, on ? 'on' : 'off', on || r.spend ? r.v(v) : esc(fill(t.locked, { amount: usd(r.need) })));
      });
    };
    let sweepRaf = 0;
    range.addEventListener('input', () => { cancelAnimationFrame(sweepRaf); set(Number(range.value)); });
    set(0);
    let swept = false;
    const sweep = () => {
      if (swept) return;
      swept = true;
      const to = Math.min(max, num(data.start, 130));
      if (reduced.matches) { set(to); return; }
      const t0 = performance.now();
      const step = (now) => {
        const k = Math.min(1, (now - t0) / 1400);
        set(to * (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2));
        if (k < 1) sweepRaf = requestAnimationFrame(step);
      };
      sweepRaf = requestAnimationFrame(step);
    };

    /* tool 2: key dates */
    const datePanel = root.querySelector('[data-plan-panel="date"]');
    const box = datePanel.querySelector('[data-plan-days]');
    const dayOut = datePanel.querySelector('[data-plan-day]');
    const days = new Map();
    const add = (d, it, kind) => { if (d == null) return; if (!days.has(d)) days.set(d, { d, opens: [], ends: [] }); days.get(d)[kind].push(it); };
    items.forEach((it) => add(dUTC(it.from), it, 'opens'));
    const fin = dUTC(data.final);
    if (fin != null) items.forEach((it) => { if (dUTC(it.to) === fin) add(fin, it, 'ends'); });
    const dayList = [...days.values()].sort((a, b) => a.d - b.d);
    const fmtD = (d, o) => new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', ...o }).format(d);
    /* a day's reminder: an hour from its first opening ("from" may carry a time, 2026-10-26 10:00, in the
       campaign timezone; without one the day starts at 00:00), listing what opens and ends, and linking
       back to the sale page (the homepage copy passes its address in as data.page) */
    const eventFor = (x) => {
      const at = (it) => { const m = /\s(\d{1,2}):(\d{2})/.exec(it.from || ''); return m ? +m[1] * 60 + +m[2] : 0; };
      const first = x.opens.length ? Math.min(...x.opens.map(at)) : 0;
      const start = x.d - tz() * 3600e3 + first * 60e3;
      const names = x.opens.map((it) => it.title).join(', ');
      const what = [...x.opens, ...x.ends].map((it) => '- ' + it.title + (it.dates ? ' (' + it.dates + ')' : '')).join('\n');
      return { title: fill(t.icsTitle, { title: names || t.dayFinal }), start, end: start + 3600e3, details: what,
        url: location.origin + (data.page || location.pathname + location.search), alarm: 0,
        file: (t.icsFile || 'sale') + '-' + new Date(x.d).toISOString().slice(0, 10) };
    };
    const showDay = (x) => {
      box.querySelectorAll('.mls-plan__dt').forEach((b) => b.setAttribute('aria-pressed', String(b === x.b)));
      dayOut.replaceChildren();
      const head = make('p', 'mls-plan__day-h');
      const diff = Math.round((x.d - today) / DAY);
      head.textContent = fmtD(x.d, { weekday: 'long', month: 'long', day: 'numeric' });
      head.append(make('small', '', diff === 0 ? t.isToday : diff > 0 ? fill(t.inDays, { n: diff }) : t.past));
      dayOut.append(head);
      [['opens', t.opens], ['ends', t.ends]].forEach(([kind, label]) => {
        if (!x[kind].length) return;
        dayOut.append(make('p', 'mls-plan__day-k', label));
        const ul = make('ul', 'mls-plan__rows');
        x[kind].forEach((it) => setRow(makeRow(it, ul), 'on', esc(it.why || it.dates)));
        dayOut.append(ul);
      });
      if (x.d >= today) {
        const a = make('button', 'mls-btn mls-btn--ghost mls-plan__ics', t.ics);
        a.type = 'button';
        a.addEventListener('click', () => cal.menu(a, eventFor(x)));
        dayOut.append(a);
      }
    };
    dayList.forEach((x) => {
      const b = make('button', 'mls-plan__dt');
      b.type = 'button';
      b.setAttribute('aria-pressed', 'false');
      const n = x.opens.length;
      const cap = x.d === fin && !n ? t.dayFinal : n === 1 ? t.dayOne : fill(t.dayCount, { count: n });
      b.append(make('span', 'mls-plan__dt-w', fmtD(x.d, { weekday: 'short' })), make('span', 'mls-plan__dt-d', fmtD(x.d, { day: 'numeric' })), make('span', 'mls-plan__dt-m', fmtD(x.d, { month: 'short' })), make('span', 'mls-plan__dt-c', cap));
      if (x.d < today) b.classList.add('is-past');
      b.addEventListener('click', () => showDay(x));
      x.b = b;
      box.append(b);
    });
    const firstDay = dayList.find((x) => x.d >= today) || dayList[dayList.length - 1];

    /* tabs */
    const tabs = [...root.querySelectorAll('[data-plan-tab]')];
    const choose = (mode, focus) => {
      tabs.forEach((b) => { const on = b.dataset.planTab === mode; b.setAttribute('aria-selected', String(on)); b.tabIndex = on ? 0 : -1; if (on && focus) b.focus(); });
      root.querySelectorAll('[data-plan-panel]').forEach((p) => { p.hidden = p.dataset.planPanel !== mode; });
      if (mode === 'save') sweep();
      if (mode === 'date' && firstDay && !dayOut.childElementCount) { showDay(firstDay); firstDay.b.scrollIntoView({ block: 'nearest', inline: 'center' }); }
    };
    tabs.forEach((b, i) => {
      b.addEventListener('click', () => choose(b.dataset.planTab));
      b.addEventListener('keydown', (e) => {
        const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
        if (d) { e.preventDefault(); choose(tabs[(i + d + tabs.length) % tabs.length].dataset.planTab, true); }
      });
    });

    /* popup */
    let place = () => {};
    const open = () => {
      if (typeof dlg.showModal !== 'function') return;
      dlg.showModal();
      document.documentElement.classList.add('mls-lock');
      choose(tabs.find((b) => b.getAttribute('aria-selected') === 'true')?.dataset.planTab || 'save');
      place();
    };
    dlg.addEventListener('close', () => { document.documentElement.classList.remove('mls-lock'); place(); });
    /* a press that started inside the box (e.g. dragging the budget slider) and ended on the backdrop is not a backdrop click */
    let downOnDlg = false;
    dlg.addEventListener('pointerdown', (e) => { downOnDlg = e.target === dlg; });
    dlg.addEventListener('click', (e) => {
      if ((e.target === dlg && downOnDlg) || e.target.closest('[data-plan-close]')) { dlg.close(); return; }
      if (e.target.closest('.mls-plan__go')) dlg.close();
    });
    launch?.addEventListener('click', open);
    if (root.id) document.querySelectorAll('a[href="#' + root.id + '"]').forEach((a) => a.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    if (!launch) return;
    /* theme editor re-renders: drop the launcher a previous render moved out */
    document.querySelectorAll('.mls-plan__launch[data-for="' + root.id + '"]').forEach((n) => n.remove());
    launch.dataset.for = root.id;
    document.body.append(launch);
    let ticking = false;
    /* data-plan-show (homepage copy): the button shows once the visitor has scrolled that % of a screen */
    const showAt = root.dataset.planShow;
    place = () => { ticking = false; launch.hidden = (showAt != null ? scrollY < innerHeight * Number(showAt) / 100 : root.getBoundingClientRect().top > 0) || dlg.open; };
    addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(place); } }, { passive: true });
    place();
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-mls-deals]').forEach(initDeals);
    (scope || document).querySelectorAll('[data-mls-plan]').forEach(initPlanner);
    tick();
  }

  init();
  window.mlsSaleInit = init;
  setInterval(() => { if (!document.hidden) tick(); }, 1000);
  /* theme editor: sections re-render in place */
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
